import { api } from '../lib/api';
import { generateQRCode } from '../lib/qrcode';
import { getStreamUrl, getNetworkIP, promptForNetworkIP } from '../lib/network';
import type { Event, User } from '../lib/api';

export async function renderDashboard(container: HTMLElement): Promise<void> {
  // Check authentication
  const user = await api.getCurrentUser();
  if (!user) {
    window.location.href = '/';
    return;
  }

  container.innerHTML = `
    <div class="dashboard">
      <header class="dashboard-header">
        <div class="header-left">
          <h1>CrowdCam</h1>
        </div>
        <div class="header-right">
          <div class="user-info">
            <img src="${user.picture}" alt="${user.name}" class="avatar">
            <span>${user.name}</span>
          </div>
          <button class="secondary" id="network-ip-btn" title="Set network IP for QR codes">
            ${getNetworkIP() ? `IP: ${getNetworkIP()}` : 'Set Network IP'}
          </button>
          <button class="secondary" id="logout-btn">Logout</button>
        </div>
      </header>

      <main class="dashboard-main">
        <div class="create-event card">
          <h2>Create New Event</h2>
          <form id="create-event-form">
            <input type="text" id="event-name" placeholder="Event name" required>
            <button type="submit" class="primary">Create Event</button>
          </form>
        </div>

        <div class="events-section">
          <h2>Your Events</h2>
          <div id="events-list" class="events-list">
            <p class="loading">Loading events...</p>
          </div>
        </div>
      </main>
    </div>

    <style>
      .dashboard {
        min-height: 100vh;
      }

      .dashboard-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 2rem;
        background: var(--color-surface);
        border-bottom: 1px solid var(--color-border);
      }

      .header-left h1 {
        font-size: 1.5rem;
        background: linear-gradient(135deg, var(--color-primary), #8b5cf6);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      .header-right {
        display: flex;
        align-items: center;
        gap: 1rem;
      }

      .user-info {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .avatar {
        width: 32px;
        height: 32px;
        border-radius: 50%;
      }

      .dashboard-main {
        padding: 2rem;
        max-width: 1200px;
        margin: 0 auto;
      }

      .create-event {
        margin-bottom: 2rem;
      }

      .create-event h2 {
        margin-bottom: 1rem;
      }

      #create-event-form {
        display: flex;
        gap: 1rem;
      }

      #create-event-form input {
        flex: 1;
        max-width: 400px;
      }

      .events-section h2 {
        margin-bottom: 1rem;
      }

      .events-list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 1rem;
      }

      .event-card {
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        border-radius: var(--radius-lg);
        padding: 1.5rem;
      }

      .event-card h3 {
        margin-bottom: 0.5rem;
      }

      .event-card .meta {
        font-size: 0.875rem;
        color: var(--color-text-muted);
        margin-bottom: 1rem;
      }

      .event-card .status {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.25rem 0.75rem;
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 500;
      }

      .event-card .status.active {
        background: rgba(34, 197, 94, 0.1);
        color: var(--color-success);
      }

      .event-card .status.inactive {
        background: rgba(239, 68, 68, 0.1);
        color: var(--color-error);
      }

      .event-card .status::before {
        content: '';
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: currentColor;
      }

      .event-card .actions {
        display: flex;
        gap: 0.5rem;
        margin-top: 1rem;
      }

      .event-card .actions button {
        flex: 1;
        padding: 0.5rem;
        font-size: 0.875rem;
      }

      .qr-modal {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 100;
      }

      .qr-modal-content {
        background: var(--color-surface);
        padding: 2rem;
        border-radius: var(--radius-lg);
        text-align: center;
        max-width: 400px;
      }

      .qr-modal-content h3 {
        margin-bottom: 1rem;
      }

      .qr-modal-content img {
        margin-bottom: 1rem;
      }

      .qr-modal-content .url {
        font-size: 0.875rem;
        color: var(--color-text-muted);
        word-break: break-all;
        margin-bottom: 1rem;
      }

      .qr-modal-content .modal-actions {
        display: flex;
        gap: 0.5rem;
      }

      .qr-modal-content .modal-actions button {
        flex: 1;
      }

      .loading {
        color: var(--color-text-muted);
      }

      .empty {
        color: var(--color-text-muted);
        text-align: center;
        padding: 2rem;
      }
    </style>
  `;

  // Load events
  await loadEvents();

  // Event handlers
  document.getElementById('logout-btn')?.addEventListener('click', async () => {
    await api.logout();
    window.location.href = '/';
  });

  document.getElementById('network-ip-btn')?.addEventListener('click', () => {
    promptForNetworkIP();
    const btn = document.getElementById('network-ip-btn');
    if (btn) {
      btn.textContent = getNetworkIP() ? `IP: ${getNetworkIP()}` : 'Set Network IP';
    }
  });

  document.getElementById('create-event-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const nameInput = document.getElementById('event-name') as HTMLInputElement;
    const name = nameInput.value.trim();
    if (!name) return;

    try {
      await api.createEvent(name);
      nameInput.value = '';
      await loadEvents();
    } catch (error) {
      alert('Failed to create event');
    }
  });
}

async function loadEvents(): Promise<void> {
  const listEl = document.getElementById('events-list');
  if (!listEl) return;

  try {
    const events = await api.getEvents();

    if (events.length === 0) {
      listEl.innerHTML = '<p class="empty">No events yet. Create one to get started!</p>';
      return;
    }

    listEl.innerHTML = events.map(event => `
      <div class="event-card" data-id="${event.id}">
        <h3>${escapeHtml(event.name)}</h3>
        <p class="meta">Created ${formatDate(event.createdAt)}</p>
        <span class="status ${event.isActive ? 'active' : 'inactive'}">
          ${event.isActive ? 'Active' : 'Ended'}
        </span>
        <div class="actions">
          ${event.isActive ? `
            <button class="secondary view-btn">View</button>
            <button class="secondary qr-btn">QR Code</button>
            <button class="secondary end-btn">End</button>
          ` : ''}
        </div>
      </div>
    `).join('');

    // Attach event handlers
    listEl.querySelectorAll('.event-card').forEach(card => {
      const id = card.getAttribute('data-id');
      if (!id) return;

      const event = events.find(e => e.id === id);
      if (!event) return;

      card.querySelector('.view-btn')?.addEventListener('click', () => {
        window.location.href = `/events/${id}`;
      });

      card.querySelector('.qr-btn')?.addEventListener('click', () => {
        showQRModal(event);
      });

      card.querySelector('.end-btn')?.addEventListener('click', async () => {
        if (confirm('Are you sure you want to end this event?')) {
          await api.deleteEvent(id);
          await loadEvents();
        }
      });
    });
  } catch (error) {
    listEl.innerHTML = '<p class="error">Failed to load events</p>';
  }
}

async function showQRModal(event: Event): Promise<void> {
  const streamUrl = getStreamUrl(event.id);
  const qrDataUrl = await generateQRCode(streamUrl);

  const modal = document.createElement('div');
  modal.className = 'qr-modal';
  modal.innerHTML = `
    <div class="qr-modal-content">
      <h3>${escapeHtml(event.name)}</h3>
      <img src="${qrDataUrl}" alt="QR Code" width="256" height="256">
      <p class="url">${streamUrl}</p>
      <div class="modal-actions">
        <button class="secondary copy-btn">Copy Link</button>
        <button class="primary download-btn">Download QR</button>
        <button class="secondary close-btn">Close</button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  modal.querySelector('.close-btn')?.addEventListener('click', () => {
    modal.remove();
  });

  modal.querySelector('.copy-btn')?.addEventListener('click', async () => {
    await navigator.clipboard.writeText(streamUrl);
    const btn = modal.querySelector('.copy-btn') as HTMLButtonElement;
    btn.textContent = 'Copied!';
    setTimeout(() => btn.textContent = 'Copy Link', 2000);
  });

  modal.querySelector('.download-btn')?.addEventListener('click', async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.src = qrDataUrl;
    await img.decode();
    canvas.width = 512;
    canvas.height = 512;
    ctx?.drawImage(img, 0, 0, 512, 512);

    const link = document.createElement('a');
    link.download = `${event.name}-qr.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  });

  modal.addEventListener('click', (e) => {
    if (e.target === modal) modal.remove();
  });
}

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
